/**
 * GEMS Admin Dashboard - Main Application
 * Light theme with Ruby and Emerald accent colors
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  Shield,
  Video,
  Gamepad2,
  UserCog,
  Activity,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  Bell,
  Coins,
  ClipboardList,
  Newspaper,
  Inbox
} from 'lucide-react';

// Admin Context
const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

// API Configuration
import { API_BASE_URL } from '../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
});

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Import Pages
import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/UsersPage';
import KPIPage from './pages/KPIPage';
import ModerationPage from './pages/ModerationPage';
import StreamsPage from './pages/StreamsPage';
import GamesPage from './pages/GamesPage';
import SpectatorBoostsPage from './pages/SpectatorBoostsPage';
import TournamentsPage from './pages/TournamentsPage';
import AdminManagementPage from './pages/AdminManagementPage';
import HealthPage from './pages/HealthPage';
import ReportsPage from './pages/ReportsPage';
import AppStoreSettingsPage from './pages/AppStoreSettingsPage';
import CurrencyManagementPage from './pages/CurrencyManagementPage';
import AgoraUsagePage from './pages/AgoraUsagePage';
import FinancialDashboardPage from './pages/FinancialDashboardPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import ModerationQueuePage from './pages/ModerationQueuePage';
import OpalPage from './pages/OpalPage';
import WorkforcePage from './pages/WorkforcePage';
import CurrentEventsPage from './pages/CurrentEventsPage';
import DecisionQueuePage from './pages/DecisionQueuePage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import WaitlistPage from './pages/WaitlistPage';
import BetaAccessPage from './pages/BetaAccessPage';
import MatchRecordsPage from './pages/MatchRecordsPage';
import ReferralsPage from './pages/ReferralsPage';
import FraudAlertsPage from './pages/FraudAlertsPage';
import PayoutsQueuePage from './pages/PayoutsQueuePage';
import EmailBroadcastPage from './pages/EmailBroadcastPage';
import ReconcilerPage from './pages/ReconcilerPage';
import AppCrashesPage from './pages/AppCrashesPage';

// Add DollarSign and Ticket icon import at the top of the file if not present
import { DollarSign, TicketIcon, Gem, ShieldCheck, Trophy, UserPlus, ShieldAlert, Wallet, Mail, Flag, Bug, Lock, Smartphone } from 'lucide-react';

// Round 26: where a notification takes you. `url` (site path, e.g. an
// escalation deep link) wins; otherwise the type decides. Paths are relative
// to the admin router's basename.
export const notificationTarget = (n) => {
  if (!n) return '/';
  if (n.url && typeof n.url === 'string') {
    const p = n.url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/admin(?=\/|\?|$)/, '');
    return p || '/';
  }
  switch (n.type) {
    case 'workforce_escalation':
      return `/workforce/${(n.agent || '').toLowerCase()}${n.escalation_id ? `?escalation=${n.escalation_id}` : ''}`;
    case 'new_support_ticket': case 'support_ticket': case 'support_ticket_reply': return '/support-tickets';
    case 'new_report': case 'stream_report': case 'report': case 'ban_appeal': return '/moderation';
    case 'fraud_alert': case 'chargeback': return '/fraud-alerts';
    case 'payout_request': case 'withdrawal': return '/payouts-queue';
    case 'app_crash': case 'client_error': return '/app-crashes';
    case 'new_user': return '/users';
    default: return '/notifications';
  }
};

// Sidebar Navigation — paths are relative to the BrowserRouter basename
const navItems = [
  // Round 36: the one screen — everything waiting on a person, from every agent.
  { icon: Inbox, label: 'Decisions', path: '/decisions', badge: 'decisions' },
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Bell, label: 'Notifications', path: '/notifications', badge: 'notifications' },
  { icon: Users, label: 'User Records', path: '/users' },
  { icon: TrendingUp, label: 'KPI Dashboard', path: '/kpi' },
  { icon: Shield, label: 'Moderation', path: '/moderation' },
  { icon: ShieldAlert, label: 'Fraud Alerts', path: '/fraud-alerts' },
  { icon: TicketIcon, label: 'Support Tickets', path: '/support-tickets' },
  { icon: Video, label: 'Streams', path: '/streams' },
  { icon: Gamepad2, label: 'Games', path: '/games' },
  { icon: Trophy, label: 'Match Records', path: '/match-records' },
  { icon: UserPlus, label: 'Referrals', path: '/referrals' },
  { icon: Gamepad2, label: 'Spectator Boosts', path: '/spectator-boosts' },
  { icon: Gamepad2, label: 'Tournaments', path: '/tournaments' },
  { icon: Coins, label: 'Currency Management', path: '/currency' },
  { icon: Video, label: 'Agora Usage', path: '/agora-usage' },
  { icon: DollarSign, label: 'Financials', path: '/financials', requiresFinancialAccess: true },
  { icon: Wallet, label: 'Payouts Queue', path: '/payouts-queue' },
  { icon: Mail, label: 'Email Broadcast', path: '/email-broadcast', requiresSuperAdmin: true },
  { icon: UserCog, label: 'Admin Management', path: '/admins' },
  { icon: ClipboardList, label: 'Waitlist', path: '/waitlist' },
  { icon: ShieldCheck, label: 'Beta Access', path: '/beta-access' },
  { icon: Gem, label: 'AI Workforce', path: '/workforce' },
  { icon: Newspaper, label: 'Current Events', path: '/current-events' },
  { icon: FileText, label: 'Reports', path: '/reports' },
  { icon: Bug, label: 'App Crashes', path: '/app-crashes' },
  { icon: Smartphone, label: 'App Store Links', path: '/app-store' },   // Round 40: where 'Get the app' sends people
];

// Round 39: per-admin access. A super admin sees everything. Everyone else
// carries `access` from the server — {all, tabs, agents} — and the tabs and
// agents they were not given are greyed out here and refused by the API.
// An admin from before Round 39 (no `access` record) keeps every tab except
// the super-admin ones, exactly as before.
const ALWAYS_TABS = ['dashboard', 'notifications'];
const SUPER_ONLY_TABS = ['email-broadcast', 'admins'];
const TAB_AGENT = { 'current-events': 'SAPPHIRE' };
export const tabKeyOf = (path) => (path === '/' || !path ? 'dashboard' : String(path).replace(/^\//, '').split('/')[0]);
export const canAccessAgent = (admin, name) => {
  if (!admin) return false;
  if (admin.role === 'super_admin') return true;
  const a = admin.access;
  if (!a || !Array.isArray(a.agents)) return true;
  return a.agents.includes(String(name || '').toUpperCase());
};
export const canAccessTab = (admin, key) => {
  if (!admin) return false;
  if (admin.role === 'super_admin') return true;
  if (SUPER_ONLY_TABS.includes(key)) return false;
  if (key === 'financials') return !!admin.financial_access;
  if (TAB_AGENT[key] && !canAccessAgent(admin, TAB_AGENT[key])) return false;
  if (ALWAYS_TABS.includes(key)) return true;
  const a = admin.access;
  if (!a || a.all) return true;
  return (a.tab_keys || a.tabs || []).includes(key);
};

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAdmin();

  // Round 36: how many decisions are waiting (refreshed every minute).
  const [decisionCount, setDecisionCount] = useState(0);
  useEffect(() => {
    if (!admin) return undefined;
    let alive = true;
    const pull = () => api.get('/api/admin/decisions/counts')
      .then((r) => { if (alive) setDecisionCount(r.data?.total || 0); })
      .catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [admin, location.pathname]);

  // Round 40: unread admin notifications on the sidebar, in step with the bell and the page.
  const [notifUnread, setNotifUnread] = useState(0);
  useEffect(() => {
    if (!admin) return undefined;
    let alive = true;
    const pull = () => api.get('/api/admin/notifications?limit=1')
      .then((r) => { if (alive) setNotifUnread(r.data?.unread_count || 0); })
      .catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    window.addEventListener('gems:admin-notifications-changed', pull);
    return () => { alive = false; clearInterval(t); window.removeEventListener('gems:admin-notifications-changed', pull); };
  }, [admin, location.pathname]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-rose-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">G</span>
            </div>
            <span className="font-bold text-lg bg-gradient-to-r from-emerald-600 to-rose-600 bg-clip-text text-transparent">
              GEMS Admin
            </span>
          </div>
          <button
            className="lg:hidden p-1 hover:bg-gray-100 rounded"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {navItems
            .map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            // Round 39: a tab this admin was not given stays in the list, greyed out.
            if (!canAccessTab(admin, tabKeyOf(item.path))) {
              return (
                <div
                  key={item.path}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 cursor-not-allowed select-none"
                  title="You don't have access to this tab — a super admin can open it for you in Admin Management"
                  data-testid={`nav-locked-${tabKeyOf(item.path)}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium flex-1">{item.label}</span>
                  <Lock className="w-3.5 h-3.5" />
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-gradient-to-r from-emerald-50 to-rose-50 text-emerald-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsOpen(false)}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : ''}`} />
                <span>{item.label}</span>
                {item.requiresFinancialAccess && (
                  <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">$</span>
                )}
                {item.badge === 'notifications' && notifUnread > 0 && (
                  <span className="ml-auto text-xs bg-rose-500 text-white px-1.5 py-0.5 rounded-full font-semibold" data-testid="nav-notifications-count">
                    {notifUnread > 99 ? '99+' : notifUnread}
                  </span>
                )}
                {item.badge === 'decisions' && decisionCount > 0 && (
                  <span className="ml-auto text-xs bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-semibold" data-testid="nav-decisions-count">
                    {decisionCount > 99 ? '99+' : decisionCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {admin?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="text-sm">
                <p className="font-medium text-gray-800 truncate max-w-[120px]">{admin?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{admin?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Header Component
const Header = ({ setIsSidebarOpen }) => {
  const { admin, api } = useAdmin();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fraudUnresolvedCount, setFraudUnresolvedCount] = useState(0);
  // Round 13: open user/filter reports — the 24-hour clock Apple asks about.
  const [openReports, setOpenReports] = useState({ open: 0, high: 0 });
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await api.get('/api/admin/notifications?limit=10');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const fetchReportCount = async () => {
    try {
      const r = await api.get('/api/admin/moderation/reports/count');
      setOpenReports({ open: r.data?.open || 0, high: r.data?.high || 0 });
    } catch (error) {
      // silently skip
    }
  };

  // Phase 4: poll unresolved chargeback count for the sidebar badge.
  const fetchFraudCount = async () => {
    try {
      const r = await api.get('/api/admin/fraud-alerts?resolved=unresolved&page=1&per_page=1');
      setFraudUnresolvedCount(r.data?.unresolved_count || 0);
    } catch (error) {
      // Non-admin or endpoint unavailable — silently skip.
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/api/admin/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const announceNotifs = () => { try { window.dispatchEvent(new Event('gems:admin-notifications-changed')); } catch (e) { /* noop */ } };

  const markAllAsRead = async () => {
    try {
      await api.post('/api/admin/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
      announceNotifs();
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  // Round 40: clear from the bell — one, or all of them
  const clearNotification = async (n) => {
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    if (!n.read) setUnreadCount(prev => Math.max(0, prev - 1));
    try { await api.delete(`/api/admin/notifications/${n.id}`); announceNotifs(); }
    catch (error) { fetchNotifications(); }
  };
  const clearAllNotifications = async () => {
    if (!window.confirm('Clear every notification? They leave the bell and the Notifications page for all admins.')) return;
    try {
      await api.post('/api/admin/notifications/clear', { scope: 'all' });
      setNotifications([]);
      setUnreadCount(0);
      announceNotifs();
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchFraudCount();
    fetchReportCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchFraudCount();
      fetchReportCount();
    }, 30000);
    // Round 40: the Notifications page / sidebar changed something — refresh the bell now
    const onChanged = () => fetchNotifications();
    window.addEventListener('gems:admin-notifications-changed', onChanged);
    return () => { clearInterval(interval); window.removeEventListener('gems:admin-notifications-changed', onChanged); };
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ban_appeal': return '⚠️';
      case 'new_report': return '🚩';
      case 'new_support_ticket': return '🎫';
      case 'new_user': return '👤';
      case 'stream_report': return '🎥';
      case 'workforce_escalation': return '🤖';
      case 'system': return '🔔';
      default: return '📢';
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-4">
        <button
          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          onClick={() => setIsSidebarOpen(true)}
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>

        {/* Search */}
        <div className="hidden md:flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 w-64">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            className="bg-transparent border-none outline-none text-sm w-full"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Phase 4: Fraud Alerts unresolved badge — separate red pill so it
            stays visible even if standard notifications are marked read. */}
        {/* Round 13: open moderation reports — red pill, links to the queue */}
        {openReports.open > 0 && (
          <Link
            to="/moderation"
            className={`relative inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${openReports.high > 0 ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-rose-500/15 border border-rose-500/40 text-rose-600 hover:bg-rose-500/25'}`}
            data-testid="sidebar-reports-badge"
            title="Open moderation reports"
          >
            <Flag className="w-3.5 h-3.5" />
            {openReports.open}
          </Link>
        )}
        {fraudUnresolvedCount > 0 && (
          <Link
            to="/fraud-alerts"
            className="relative inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-600 text-xs font-medium hover:bg-red-500/25"
            data-testid="sidebar-fraud-badge"
            title="Unresolved chargeback alerts"
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            {fraudUnresolvedCount}
          </Link>
        )}

        {/* Notifications */}
        <div className="relative">
          <button 
            className="relative p-2 hover:bg-gray-100 rounded-lg"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell className="w-5 h-5 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
              <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
                <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      Mark all as read
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAllNotifications}
                      className="text-xs text-rose-600 hover:text-rose-700 font-medium"
                      data-testid="bell-clear-all"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>
              
              <div className="max-h-80 overflow-y-auto">
                {loadingNotifications ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="animate-spin w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No notifications yet</p>
                    <p className="text-gray-400 text-xs mt-1">You&apos;ll see ban appeals and system alerts here</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${
                        !notification.read ? 'bg-emerald-50/50' : ''
                      }`}
                      onClick={() => {
                        // Round 26: a notification opens what it is about
                        if (!notification.read) markAsRead(notification.id);
                        setShowNotifications(false);
                        navigate(notificationTarget(notification));
                      }}
                      data-testid={`bell-notif-${notification.id}`}
                    >
                      <div className="flex gap-3">
                        <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notification.title || (notification.type === 'new_support_ticket' ? `New ${notification.category || 'support'} ticket` : notification.type === 'new_report' ? 'New report' : 'Notification')}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {notification.message || notification.subject || (notification.user_name ? `from ${notification.user_name}` : '')}
                          </p>
                          {notification.suggested_action && (
                            <p className="text-xs text-emerald-700 mt-0.5 line-clamp-2" data-testid={`bell-action-${notification.id}`}>
                              <span className="font-semibold">Suggested: </span>{notification.suggested_action}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {getTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-1"></span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); clearNotification(notification); }}
                          title="Clear"
                          className="text-gray-300 hover:text-rose-600 flex-shrink-0 -mt-0.5"
                          data-testid={`bell-clear-${notification.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {notifications.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <Link
                    to="/notifications"
                    className="block text-center text-sm text-emerald-600 hover:text-emerald-700 font-medium py-1"
                    onClick={() => setShowNotifications(false)}
                  >
                    View all notifications
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User dropdown */}
        <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-gray-200">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {admin?.name?.charAt(0) || 'A'}
            </span>
          </div>
          <div className="text-sm">
            <p className="font-medium text-gray-800">{admin?.name}</p>
          </div>
        </div>
      </div>
    </header>
  );
};

// Main Layout
const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <div className="lg:ml-64">
        <Header setIsSidebarOpen={setIsSidebarOpen} />
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
};

// Round 39: what an admin sees when they open a tab or agent they were not given.
const NoAccess = ({ what }) => (
  <div className="min-h-[50vh] flex items-center justify-center" data-testid="admin-no-access">
    <div className="text-center max-w-md px-4">
      <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
      <h2 className="text-lg font-bold text-gray-900">You don't have access to this {what || 'tab'}</h2>
      <p className="text-sm text-gray-500 mt-1">A super admin can open it for you from Admin Management.</p>
      <Link to="/" className="inline-block mt-4 text-sm text-emerald-700 font-medium hover:underline">Back to the dashboard</Link>
    </div>
  </div>
);

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAdmin();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!admin) {
    // Round 26: come back to the page the link pointed at after signing in
    // (a push / email deep link used to dump admins on the dashboard).
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'} replace />;
  }

  // Round 39: the route itself is guarded, not just the sidebar (deep links,
  // typed URLs). The API refuses the same paths server-side.
  const segs = location.pathname.split('/').filter(Boolean);
  const tabKey = tabKeyOf(location.pathname);
  if (!canAccessTab(admin, tabKey)) {
    return <AdminLayout><NoAccess what="tab" /></AdminLayout>;
  }
  if (tabKey === 'workforce' && segs[1] && !canAccessAgent(admin, segs[1])) {
    return <AdminLayout><NoAccess what="agent" /></AdminLayout>;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

// Admin Provider
const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/api/admin/me');
      setAdmin(response.data);
    } catch (error) {
      localStorage.removeItem('admin_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/admin/login', { email, password });
      const { session_token, user } = response.data;
      localStorage.setItem('admin_token', session_token);
      setAdmin(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/admin/logout');
    } catch (error) {
      // Proceed with client-side cleanup even if server logout fails
    }
    localStorage.removeItem('admin_token');
    setAdmin(null);
  };

  return (
    <AdminContext.Provider value={{ admin, loading, login, logout, api }}>
      {children}
    </AdminContext.Provider>
  );
};

// Main Admin App — accepts basename prop for standalone BrowserRouter
const AdminApp = ({ basename }) => {
  const routes = (
    <AdminProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<AdminLogin />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><AdminNotificationsPage /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
        <Route path="/kpi" element={<ProtectedRoute><KPIPage /></ProtectedRoute>} />
        <Route path="/moderation" element={<ProtectedRoute><ModerationPage /></ProtectedRoute>} />
        <Route path="/moderation-queue" element={<ProtectedRoute><ModerationQueuePage /></ProtectedRoute>} />
        <Route path="/fraud-alerts" element={<ProtectedRoute><FraudAlertsPage /></ProtectedRoute>} />
        <Route path="/support-tickets" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />
        <Route path="/streams" element={<ProtectedRoute><StreamsPage /></ProtectedRoute>} />
        <Route path="/games" element={<ProtectedRoute><GamesPage /></ProtectedRoute>} />
        <Route path="/match-records" element={<ProtectedRoute><MatchRecordsPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
        <Route path="/spectator-boosts" element={<ProtectedRoute><SpectatorBoostsPage /></ProtectedRoute>} />
        <Route path="/tournaments" element={<ProtectedRoute><TournamentsPage /></ProtectedRoute>} />
        <Route path="/currency" element={<ProtectedRoute><CurrencyManagementPage /></ProtectedRoute>} />
        <Route path="/agora-usage" element={<ProtectedRoute><AgoraUsagePage /></ProtectedRoute>} />
        <Route path="/admins" element={<ProtectedRoute><AdminManagementPage /></ProtectedRoute>} />
        <Route path="/financials" element={<ProtectedRoute><FinancialDashboardPage /></ProtectedRoute>} />
        <Route path="/payouts-queue" element={<ProtectedRoute><PayoutsQueuePage /></ProtectedRoute>} />
        <Route path="/email-broadcast" element={<ProtectedRoute><EmailBroadcastPage /></ProtectedRoute>} />
        <Route path="/reconciler" element={<ProtectedRoute><ReconcilerPage /></ProtectedRoute>} />
        <Route path="/health" element={<ProtectedRoute><HealthPage /></ProtectedRoute>} />
        <Route path="/waitlist" element={<ProtectedRoute><WaitlistPage /></ProtectedRoute>} />
                <Route path="/beta-access" element={<ProtectedRoute><BetaAccessPage /></ProtectedRoute>} />
        <Route path="/workforce" element={<ProtectedRoute><WorkforcePage /></ProtectedRoute>} />
        <Route path="/workforce/:agentName" element={<ProtectedRoute><WorkforcePage /></ProtectedRoute>} />
        <Route path="/current-events" element={<ProtectedRoute><CurrentEventsPage /></ProtectedRoute>} />
        <Route path="/decisions" element={<ProtectedRoute><DecisionQueuePage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/app-store" element={<ProtectedRoute><AppStoreSettingsPage /></ProtectedRoute>} />
        <Route path="/app-crashes" element={<ProtectedRoute><AppCrashesPage /></ProtectedRoute>} />
      </Routes>
    </AdminProvider>
  );

  // When basename is provided, wrap in BrowserRouter (standalone mode)
  if (basename !== undefined) {
    return <BrowserRouter basename={basename}>{routes}</BrowserRouter>;
  }

  // Legacy nested mode (no BrowserRouter, parent provides it)
  return routes;
};

export default AdminApp;
export { api };
